import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetScannerQuery as GetScanner, PurchaseStatus } from '__generated__/graphql';
import { encodeUrl, getServerErrors } from '../../../common/helpers';
import { getRegistrationFiltersInput } from '../../../common/segmentHelpers';
import RegistrationFilters, { RegistrationFilterParams } from '../Registrations/RegistrationFilters';
import UI from '../../../common/UI';
import useEditScannerMutation from './useEditScannerMutation';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

type Scanner = GetScanner['project']['scanner'];

const editScannerCriteriaSchema = yup.object().shape({});

export interface EditScannerCriteriaFormProps {
  scanner: Scanner;
}

const EditScannerCriteriaForm = ({ scanner }: EditScannerCriteriaFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const getValues = (scanner: Scanner) => (
    scanner.segment.registration_filters
      ? getRegistrationFiltersInput(scanner.segment.registration_filters)
      : {}
  );

  const form = useForm({
    values: getValues(scanner),
  });

  useNavigationPrompt(form.changed());

  const [editScanner, { error, loading }] = useEditScannerMutation({
    input: {
      id: scanner.id,
      filters: {
        ticket_categories: form.values.ticket_categories,
        tickets: form.values.tickets,
        time_slots: form.values.time_slots,
        products: form.values.products,
        product_variants: form.values.product_variants,
        promotions: form.values.promotions,
        participant_fields: form.values.participant_fields,
        participant_field_choices: form.values.participant_field_choices,
        invitations: form.values.invitations,
        coupons: form.values.coupons,
        ticket_number_sequences: form.values.ticket_number_sequences,
        has_ticket_number: form.values.has_ticket_number,
        has_team: form.values.has_team,
        genders: form.values.genders,
        min_age: form.values.min_age,
        max_age: form.values.max_age,
      },
    },
    onSuccess: (scanner) => {
      form.reset(getValues(scanner));
    },
  });

  const filters = {
    ...form.values,
    event: scanner.event.id,
    statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
  };

  const handleFiltersChange = (update: (filters: RegistrationFilterParams) => RegistrationFilterParams) => {
    form.set((values) => {
      const newValues = update(values);

      return {
        ticket_categories: newValues.ticket_categories || [],
        tickets: newValues.tickets || [],
        time_slots: newValues.time_slots || [],
        products: newValues.products || [],
        product_variants: newValues.product_variants || [],
        promotions: newValues.promotions || [],
        participant_fields: newValues.participant_fields || [],
        participant_field_choices: newValues.participant_field_choices || [],
        invitations: newValues.invitations || [],
        coupons: newValues.coupons || [],
        ticket_number_sequences: newValues.ticket_number_sequences,
        has_ticket_number: newValues.has_ticket_number,
        has_team: newValues.has_team,
        genders: newValues.genders,
        min_age: newValues.min_age,
        max_age: newValues.max_age,
      };
    });
  };

  const { valid } = useValidation({
    schema: editScannerCriteriaSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editScanner()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />
        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('criteria')}</UI.Legend>
          <UI.InputDescription>
            {t('scanner_criteria_description')}
          </UI.InputDescription>
        </UI.Div>
        <RegistrationFilters
          filters={filters}
          onChange={handleFiltersChange}
          types={[
            'tickets', 'ticket_number_sequences', 'products', 'participant_fields', 'invitations', 'coupons', 'team',
          ]}
          showResetButton={false}
          showViewParticipantsButton
        />
        <UI.HR />
        <UI.FormButtons
          form={form}
          submitLabel={t('save_scanner')}
          closeTo={`/${project.id}/participants/scanners?${encodeUrl({ event: scanner.event.id })}`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditScannerCriteriaForm;
